import { Controller } from "@hotwired/stimulus"
import { fireNumber, monthlyRate, monthsToTarget, project, realReturn } from "../fire_math"
import { track } from "./analytics_controller"
import { currencyFromQuery, drawChart, duration, line, money, monthYear, nextCurrency, percent, renderLevers, renderSteps, saveQuery, shortDuration, yearLabels } from "../fire_view"

export default class extends Controller {
  static targets = ["currency", "form", "advanced", "real", "number", "multiple", "duration", "date", "progress", "bar", "canvas", "milestones", "levers", "milestoneTemplate", "leverTemplate"]

  connect() {
    this.currency = currencyFromQuery()
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(false)
  }

  cycle() {
    this.currency = nextCurrency(this.currency)
    this.currencyTargets.forEach((target) => { target.textContent = this.currency.symbol })
    this.update(true)
  }

  money(value) {
    return money(value, this.currency)
  }

  disconnect() {
    clearTimeout(this.timer)
    this.chart?.destroy()
  }

  submit(event) {
    event.preventDefault()
  }

  change() {
    this.update(true)
  }

  read(name) {
    const value = parseFloat(this.formTarget.elements[name].value)
    return Number.isFinite(value) ? value : null
  }

  inputs() {
    return {
      spending: Math.max(0, this.read("spending") || 0),
      portfolio: Math.max(0, this.read("portfolio") || 0),
      monthly: Math.max(0, this.read("monthly") || 0),
      nominal: (this.read("return") || 0) / 100,
      withdrawal: (this.read("withdrawal") || 0) / 100,
      inflation: (this.read("inflation") || 0) / 100,
      fees: (this.read("fees") || 0) / 100,
      raise: (this.read("raise") || 0) / 100,
      tax: Math.min(0.99, Math.max(0, (this.read("tax") || 0) / 100)),
      age: this.read("age")
    }
  }

  update(record) {
    const inputs = this.inputs()
    const annual = realReturn(inputs)
    const target = fireNumber(inputs.spending, inputs.withdrawal, inputs.tax)
    const plan = { portfolio: inputs.portfolio, monthly: inputs.monthly, annual, raise: inputs.raise }
    const months = target > 0 ? monthsToTarget({ ...plan, target }) : null
    const horizon = months === null ? 40 : Math.min(60, Math.max(10, Math.ceil(months / 12) + 5))
    const points = project({ ...plan, months: horizon * 12 })
    const context = { inputs, annual, target, plan, months, points }

    this.realTarget.textContent = `About ${percent(annual)} a year after inflation${inputs.fees > 0 ? " and fees" : ""}.`
    this.renderSummary(context)
    this.renderChart(context, horizon)
    this.renderMilestones(context)
    this.renderLevers(context)

    if (record) this.record(months)
  }

  record(months) {
    saveQuery(this.formTarget, this.currency)

    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      track("fire-run", { currency: this.currency.code, years: months === null ? "never" : Math.floor(months / 12), advanced: this.advancedTarget.open })
    }, 1500)
  }

  ageAt(months) {
    const { age } = this.inputs()
    return age === null ? null : Math.floor(age + months / 12)
  }

  renderSummary({ inputs, target, months }) {
    const progress = target > 0 ? Math.min(1, inputs.portfolio / target) : 0

    this.numberTarget.textContent = target > 0 ? this.money(target) : "—"
    if (target === 0) this.multipleTarget.textContent = "Add your spending and a withdrawal rate"
    else if (inputs.tax > 0) this.multipleTarget.textContent = `Enough for ${this.money(inputs.spending)} a year after ${percent(inputs.tax)} tax`
    else this.multipleTarget.textContent = `${Number((1 / inputs.withdrawal).toFixed(1))} times your yearly spending`

    if (months === null) {
      this.durationTarget.textContent = "Out of reach"
      this.dateTarget.textContent = target > 0 ? "Save more or expect a higher return" : ""
    } else if (months === 0) {
      this.durationTarget.textContent = "You're there"
      this.dateTarget.textContent = "You already have your FIRE number"
    } else {
      const age = this.ageAt(months)
      this.durationTarget.textContent = shortDuration(months)
      this.dateTarget.textContent = `Around ${monthYear(months)}${age === null ? "" : `, at ${age}`}`
    }

    this.progressTarget.textContent = `${Math.round(progress * 100)}%`
    this.barTarget.style.width = `${progress * 100}%`
  }

  renderChart({ target, points }, horizon) {
    const yearly = points.filter((point) => point.month % 12 === 0)
    this.chart = drawChart(this.chart, this.canvasTarget, {
      labels: yearLabels(points),
      horizon,
      currency: this.currency,
      datasets: [
        line("Portfolio", yearly.map((point) => point.value), "#00d6b4", { backgroundColor: "rgba(0, 214, 180, 0.08)", fill: "origin", pointHoverRadius: 4 }),
        line("Money put in", yearly.map((point) => point.invested), "rgba(255, 255, 255, 0.4)", { borderDash: [6, 6] }),
        line("FIRE number", yearly.map(() => target), "#e14eca")
      ]
    })
  }

  away(months) {
    const age = this.ageAt(months)
    return `${duration(months)} from today${age === null ? "" : `, age ${age}`}`
  }

  renderMilestones({ inputs, target, plan, months, points }) {
    const share = target > 0 ? Math.round(Math.min(1, inputs.portfolio / target) * 100) : 0
    const steps = []

    if (months > 0) {
      [0.25, 0.5, 0.75].forEach((fraction) => {
        const reached = monthsToTarget({ ...plan, target: target * fraction })
        if (reached > 0) steps.push({ kind: "milestone", month: reached, title: `${fraction * 100}% of the way`, detail: this.away(reached), amount: target * fraction })
      })

      const rate = monthlyRate(plan.annual)
      const crossover = rate > 0 ? points.findIndex((point) => point.month > 0 && point.saving > 0 && point.value * rate >= point.saving) : -1
      if (crossover > 0 && crossover < months) {
        steps.push({ kind: "crossover", month: crossover, title: "Your money starts earning more than you save", detail: `Growth passes ${this.money(points[crossover].saving)} a month`, amount: points[crossover].value })
      }

      steps.push({ kind: "fire", month: months, title: "FIRE", detail: `Enough to withdraw ${this.money(inputs.spending)} a year`, amount: target })
    } else if (months === null && target > 0) {
      steps.push({ kind: "fire", month: Infinity, when: "Not yet", title: "FIRE", detail: "Out of reach at this pace", amount: target })
    }

    const rows = [
      { kind: "today", when: "Today", title: "Where you are now", detail: months === 0 ? "You already have your FIRE number" : `${share}% of your FIRE number`, amount: inputs.portfolio },
      ...steps.sort((first, second) => first.month - second.month)
    ]

    renderSteps(this.milestonesTarget, this.milestoneTemplateTarget, rows, this.currency)
  }

  renderLevers({ inputs, annual, target, plan, months }) {
    if (target === 0) {
      this.leversTarget.replaceChildren()
      return
    }

    const extraSaving = 250 * this.currency.scale
    const spendingCut = 3000 * this.currency.scale
    const lowerSpending = inputs.spending - spendingCut
    const lowerWithdrawal = inputs.withdrawal - 0.005
    const levers = [
      { label: `Save ${this.money(extraSaving)} more a month`, detail: `${this.money(inputs.monthly + extraSaving)} a month`, months: monthsToTarget({ ...plan, monthly: inputs.monthly + extraSaving, target }) },
      lowerSpending > 0 && { label: `Spend ${this.money(spendingCut)} less a year`, detail: `FIRE number drops to ${this.money(fireNumber(lowerSpending, inputs.withdrawal, inputs.tax))}`, months: monthsToTarget({ ...plan, target: fireNumber(lowerSpending, inputs.withdrawal, inputs.tax) }) },
      { label: "Earn 1% less a year", detail: `About ${percent(annual - 0.01)} after inflation`, months: monthsToTarget({ ...plan, annual: annual - 0.01, target }) },
      lowerWithdrawal > 0 && { label: `Withdraw ${percent(lowerWithdrawal)} instead`, detail: `FIRE number rises to ${this.money(fireNumber(inputs.spending, lowerWithdrawal, inputs.tax))}`, months: monthsToTarget({ ...plan, target: fireNumber(inputs.spending, lowerWithdrawal, inputs.tax) }) }
    ].filter(Boolean)

    renderLevers(this.leversTarget, this.leverTemplateTarget, months, levers)
  }
}
